import React, { useState, Suspense } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Mail,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";

useGLTF.preload("/models/2014_lamborghini_veneno_roadster.glb");

function Model() {
  const { scene } = useGLTF("/models/2014_lamborghini_veneno_roadster.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      style={{ width: "100%", height: "100%" }}
      className="w-full h-full"
    >
      <color attach="background" args={["#0f172a"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate autoRotateSpeed={0.5} enableZoom={false} />
      </Suspense>
    </Canvas>
  );
}

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { resetPassword, loading, error, setError } = useAuth();

  const iconVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
      },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setError("");

    if (!email) {
      setError("Veuillez entrer votre adresse email");
      return;
    }

    const success = await resetPassword(email);

    if (success) {
      setSuccessMessage(
        "Un lien de réinitialisation a été envoyé à votre adresse email"
      );
      setEmail("");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 py-32 relative overflow-hidden">
      {/* Canvas 3D */}
      <div className="fixed inset-0 z-0" style={{ zIndex: 0 }}>
        <CarScene />
      </div>

      {/* Loader */}
      <Loader />

      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-1" style={{ zIndex: 1 }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="w-full max-w-md relative z-10"
        style={{ zIndex: 10 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-block mb-4"
          >
            <div className="bg-gradient-to-br from-orange-600 to-orange-500 p-4 rounded-full shadow-lg shadow-orange-500/50">
              <Mail className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white to-orange-300"
          >
            Mot de passe oublié
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-slate-400 text-xs"
          >
            Réinitialiser votre mot de passe en quelques étapes
          </motion.p>
        </div>

        {/* Formulaire Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-slate-900/80 backdrop-blur-xl p-6 rounded-2xl shadow-2xl border border-slate-700/50"
        >
          {/* Messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-3 rounded-lg mb-4 flex items-center gap-2 text-xs"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-900/50 border border-green-700 text-green-200 px-3 py-3 rounded-lg mb-4 flex items-center gap-2 text-xs"
            >
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>{successMessage}</span>
            </motion.div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="flex text-xs font-medium text-white mb-2 items-center gap-2">
                <motion.div variants={iconVariants} animate="animate">
                  <Mail className="w-4 h-4 text-orange-400" />
                </motion.div>
                Votre email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:outline-none transition-all text-sm disabled:opacity-50"
                placeholder="vous@exemple.com"
                required
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-blue-900/30 border border-blue-700/40 rounded-lg p-3 mt-5"
            >
              <p className="text-xs text-blue-200">
                💡 Nous vous enverrons un lien pour réinitialiser votre mot de
                passe
              </p>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full mt-8 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <span>Envoyer le lien</span>
                  <Mail className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Retour lien */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-6 pt-6 border-t border-slate-700"
          >
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-300 text-xs transition-colors font-medium"
            >
              <ArrowLeft className="w-3 h-3" />
              Retour à la connexion
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;